// Words a person would say, in this operation's vocabulary.
//
// Nothing in this app hardcodes "delivery". A profile decides what one
// unit of work is called, who is waiting at the other end, and what the
// person driving is — and every screen asks here rather than deciding
// for itself, because a school transport office reading "job sealed to
// driver" is reading somebody else's product.

import { profileFor, word, type ProfileId, type StopFieldId } from "@wayfind/core";

export type Vocabulary = ReturnType<typeof vocabularyFor>;

export function vocabularyFor(profile: ProfileId) {
  const definition = profileFor(profile);
  return {
    profile,
    definition,
    job: word(profile, "job"),
    jobs: word(profile, "jobs"),
    recipient: word(profile, "recipient"),
    driver: word(profile, "driver"),
    stop: word(profile, "stop"),
    run: word(profile, "run"),
    /**
     * Rung 3's object, which is **not** rung 1's.
     *
     * A round is a day's sequence; a route is a thing with a term, a
     * driver who may change, and a link the recipient keeps. Three of
     * the four profiles call the first one a "round", and letting the
     * second one borrow that word would make the app say "make this
     * round a round".
     */
    route: "route",
    Job: capital(word(profile, "job")),
    Jobs: capital(word(profile, "jobs")),
    Recipient: capital(word(profile, "recipient")),
    Driver: capital(word(profile, "driver")),
    Stop: capital(word(profile, "stop")),
    Run: capital(word(profile, "run")),
    Route: "Route"
  };
}

function capital(text: string): string {
  return text.slice(0, 1).toUpperCase() + text.slice(1);
}

/**
 * More than one of something, in English, without a dependency.
 * "delivery" pluralises to "deliveries" and "deliverys" is the kind of
 * detail that tells an operator nobody read this screen.
 */
export function plural(count: number, word: string): string {
  if (count === 1) return `1 ${word}`;
  if (/[^aeiou]y$/u.test(word)) return `${count} ${word.slice(0, -1)}ies`;
  if (/(s|x|z|ch|sh)$/u.test(word)) return `${count} ${word}es`;
  return `${count} ${word}s`;
}

/**
 * The **outcomes** this profile's drivers will be able to assert at a
 * door, in this profile's words (§5.2.1 reason codes, unchanged on the
 * wire). The console shows them so a dispatcher knows what record they
 * are going to get back — and, for a pharmacy, which one is off the
 * table for a stop that needs an identity check.
 */
export interface OutcomeWords {
  code: number;
  reason: number;
  text: string;
  /** Why this one cannot be picked for this stop, when it cannot. */
  forbiddenWhen?: "identityCheck";
}

export function outcomeVocabulary(profile: ProfileId): OutcomeWords[] {
  const common: OutcomeWords[] = [
    { code: 1, reason: 0, text: "Handed over" },
    { code: 3, reason: 1, text: "Nobody home" },
    { code: 3, reason: 2, text: "Refused at the door" },
    { code: 2, reason: 3, text: "Could not get to the door" }
  ];
  switch (profile) {
    case "restaurant":
      return [
        { code: 1, reason: 0, text: "Handed to the customer" },
        { code: 1, reason: 0, text: "Left at the door as asked" },
        { code: 3, reason: 1, text: "Nobody home" },
        { code: 3, reason: 2, text: "Customer refused it" },
        { code: 2, reason: 3, text: "Could not get to the door" }
      ];
    case "pharmacy":
      return [
        { code: 1, reason: 0, text: "Handed to the named recipient, ID checked" },
        {
          code: 1,
          reason: 0,
          text: "Left with a neighbour",
          forbiddenWhen: "identityCheck"
        },
        { code: 3, reason: 1, text: "Nobody home — returned to the pharmacy" },
        { code: 3, reason: 2, text: "Recipient refused it — returned to the pharmacy" },
        { code: 3, reason: 4, text: "Item missing from the bag" },
        { code: 2, reason: 3, text: "Could not get to the door" }
      ];
    case "school":
      return [
        { code: 1, reason: 0, text: "Stop cleared" },
        { code: 2, reason: 3, text: "Could not reach the stop" },
        { code: 3, reason: 1, text: "Nobody waiting" }
      ];
    default:
      return [...common, { code: 3, reason: 4, text: "Parcel missing" }];
  }
}

/** The label a stop field gets, from the profile that declares it. */
export function fieldLabel(profile: ProfileId, id: StopFieldId): string {
  return profileFor(profile).stopFields.find(field => field.id === id)?.label ?? id;
}

/** Minutes, hours and days, said the way a person says them. */
export function relativeWords(seconds: number): string {
  const abs = Math.abs(seconds);
  if (abs < 90) return seconds >= 0 ? "just now" : "in a moment";
  const minutes = Math.round(abs / 60);
  if (minutes < 60) return seconds >= 0 ? `${minutes} min ago` : `in ${minutes} min`;
  const hours = Math.round(minutes / 60);
  if (hours < 36) return seconds >= 0 ? `${hours} h ago` : `in ${hours} h`;
  const days = Math.round(hours / 24);
  return seconds >= 0 ? `${days} days ago` : `in ${days} days`;
}

export function clockWords(unixSeconds: number): string {
  return new Date(unixSeconds * 1000).toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit"
  });
}

export function dateWords(unixSeconds: number): string {
  return new Date(unixSeconds * 1000).toLocaleDateString([], {
    weekday: "short",
    day: "numeric",
    month: "short"
  });
}

export function distanceWords(meters: number): string {
  return meters < 1000 ? `${Math.round(meters)} m` : `${(meters / 1000).toFixed(1)} km`;
}

export function durationWords(seconds: number): string {
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} min`;
  return `${Math.floor(minutes / 60)} h ${String(minutes % 60).padStart(2, "0")}`;
}
